import React from 'react';

export interface IOption {
    name: string;
    shortName: string;
}

interface ITypeAheadInputProps {
    label: JSX.Element;
    name: string;
    errorID: string;
    minimumInput: number;
    query: string;
    onQueryChange: (str: string) => void;
    getOptions: (str: string) => Promise<IOption[]>;
    onOptionSelection?: (option: IOption, shouldSwitchControl?: boolean) => void;
    errorMessage?: string;
    preventTab?: boolean;
    placeHolder?: string;
    inputRef?: React.MutableRefObject<HTMLInputElement | null>;
}

const TypeAheadInput = (props: ITypeAheadInputProps) => {
    const {
        label,
        name,
        errorID,
        query,
        errorMessage,
        getOptions,
        onOptionSelection,
        minimumInput,
        onQueryChange,
        preventTab,
        placeHolder
    } = props;

    const [showOptions, setShowOptions] = React.useState(false);
    const [options, setOptions] = React.useState<IOption[]>([]);
    const [isLoading, setLoading] = React.useState<boolean>(false);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = React.useState<number | undefined>(undefined);

    const selected = React.useRef(false);
    const lastUserInput = React.useRef<string>('');
    const inputRef = React.useRef<HTMLInputElement>(null);
    const oldTimer = React.useRef<NodeJS.Timer | null>(null);
    const activeSuggestion = React.useRef<HTMLLIElement>(null);
    const shouldSelect = React.useRef<boolean>(false);
    activeSuggestion.current?.scrollIntoView({ block: 'center', behavior: 'auto' });
    props.inputRef && (props.inputRef.current = inputRef.current);

    React.useEffect(() => {
        setActiveSuggestionIndex(undefined);
    }, [options]);
    React.useEffect(() => {
        if (activeSuggestionIndex !== undefined) {
            onQueryChange(options[activeSuggestionIndex].name);
        }
    }, [activeSuggestionIndex]);

    const optionSelectionInternal = (option?: IOption, shouldSwitchControl?: boolean) => {
        if (!option) {
            if (activeSuggestionIndex !== undefined) {
                option = options[activeSuggestionIndex];
            } else if (options.length === 1) {
                option = options[0];
            }
        }
        if (option) {
            onQueryChange(option.name);
            selected.current = true;
            setOptions([]);
            onOptionSelection && onOptionSelection(option, shouldSwitchControl);
            return true;
        }
        return false;
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (shouldSelect.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        // User pressed the tab key
        if (e.keyCode === 9) {
            if (e.shiftKey) {
                return;
            }
            preventTab && (e.preventDefault(), e.stopPropagation());
            !optionSelectionInternal(undefined, true);
        }

        // User pressed the enter key
        else if (e.keyCode === 13) {
            e.preventDefault();
            e.stopPropagation();
            if (oldTimer.current) shouldSelect.current = true;
            else optionSelectionInternal();
        }
        // User pressed the up arrow
        else if (e.keyCode === 38) {
            e.preventDefault();
            if (activeSuggestionIndex) {
                setActiveSuggestionIndex(activeSuggestionIndex - 1);
            } else if (activeSuggestionIndex === 0) {
                oldTimer.current && clearTimeout(oldTimer.current);
                setActiveSuggestionIndex(undefined);
                onQueryChange(lastUserInput.current);
            }
        }
        // User pressed the down arrow
        else if (e.keyCode === 40) {
            e.preventDefault();
            if (activeSuggestionIndex === undefined && options.length > 0) {
                setActiveSuggestionIndex(0);
                lastUserInput.current = query;
            } else if (activeSuggestionIndex! < options.length - 1) {
                setActiveSuggestionIndex(activeSuggestionIndex! + 1);
            }
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value.toUpperCase();
        if (newValue.length >= minimumInput) {
            oldTimer.current && clearTimeout(oldTimer.current);
            const timer = setTimeout(() => {
                setLoading(true);
                setShowOptions(false);
                const promise = getOptions(newValue);
                promise.then(options => {
                    if (timer === oldTimer.current) {
                        if (options.length === 1 && shouldSelect.current) {
                            optionSelectionInternal(options[0]);
                            setOptions([]);
                            setLoading(false);
                        } else {
                            setOptions(options);
                            setLoading(false);
                            setShowOptions(true);
                        }
                        shouldSelect.current = false;
                        oldTimer.current = null;
                    }
                });
            }, 500);
            oldTimer.current = timer;
        } else {
            oldTimer.current && clearTimeout(oldTimer.current);
            oldTimer.current = null;
            setOptions([]);
        }
        selected.current = false;
        onQueryChange(newValue);
    };
    const renderOptions = () => {
        if (options.length) {
            return (
                <ul className='address-options' onMouseDown={e => e.preventDefault()}>
                    {options.map((option, index) => (
                        <li
                            className={index === activeSuggestionIndex ? 'suggest-active' : ''}
                            onClick={() => (optionSelectionInternal(option), inputRef.current?.focus())}
                            ref={index === activeSuggestionIndex ? activeSuggestion : null}
                        >
                            {option.name}
                        </li>
                    ))}
                </ul>
            );
        }
        return;
    };

    return (
        <div className='form-group'>
            {label}
            {isLoading && <span className='cui-inline-spinner' onMouseDown={e => e.preventDefault()}></span>}
            <input
                name={name}
                id={name}
                aria-describedby={errorID}
                value={query}
                onKeyDown={onKeyDown}
                className='form-field'
                type='text'
                autoComplete='off'
                onChange={onChange}
                ref={inputRef}
                onFocus={() => setShowOptions(true)}
                onBlur={() => setShowOptions(false)}
                placeholder={placeHolder}
            />
            {errorMessage ? (
                <div id={errorID} className='cui alert-small error'>
                    {errorMessage}
                </div>
            ) : (
                showOptions && renderOptions()
            )}
        </div>
    );
};

export default TypeAheadInput;
